// PATH: src/pages/AdministrationPage.tsx — Phase 5 : export Massar (mapping
// reconfigurable), gestion documentaire, bibliothèque, transport scolaire.
import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Card, Checkbox, DatePicker, Divider, Form, Input, InputNumber, List, Popconfirm,
  Segmented, Select, Space, Tabs, Tag, Typography, message,
} from "antd";
import { DownloadOutlined, WhatsAppOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useApiClient } from "../api/apiClients";
import { useSchoolApi } from "../api/school";
import { WHATSAPP_ENABLED } from "../config/features";
import { usePedagogyApi, type Staff } from "../api/pedagogy";
import DocumentUpload from "../components/DocumentUpload";
import { useTabParam } from "../hooks/useTabParam";
import ResponsiveTable from "../components/ResponsiveTable";
import type { SchoolYear, Student } from "../models/school";

const apiError = (err: any) => message.error(err?.response?.data?.error || "Opération refusée", 5);

const MASSAR_SOURCES = [
  "massar_code", "last_name", "first_name", "last_name_ar", "first_name_ar", "gender",
  "birth_date", "birth_place", "cin", "address", "city", "year_label", "class_label",
  "level_code", "level_name", "enrollment_status",
];

const AdministrationPage: React.FC = () => {
  const school = useSchoolApi();
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [tab, setTab] = useTabParam("export");
  useEffect(() => { school.listYears().then(setYears).catch(() => undefined); }, [school]);

  return (
    <div>
      <Typography.Title level={3}>Administration & vie scolaire</Typography.Title>
      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: "export", label: "Export CSV", children: <ExportTab years={years} /> },
        { key: "massar", label: "Export Massar (format officiel)", children: <MassarTab years={years} /> },
        { key: "docs", label: "Documents", children: <DocsTab years={years} /> },
        { key: "biblio", label: "Bibliothèque", children: <LibraryTab /> },
        { key: "transport", label: "Transport", children: <TransportTab /> },
      ]} />
    </div>
  );
};

// ── Export CSV (généré côté navigateur) ─────────────────────────────────────
// Le backend n'exporte que les élèves d'une année (endpoint Massar). Pour
// filtrer par classe/matière et exporter aussi le personnel SANS toucher au
// backend, on assemble le CSV ici, à partir des listes que l'UI charge déjà.

// esc : échappement CSV. Point-virgule comme séparateur (locale FR/Excel Maroc),
// donc on protège ; " et les sauts de ligne.
const escCsv = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// buildCsv : BOM + CRLF pour qu'Excel lise l'UTF-8 (indispensable aux noms en
// arabe) et reconnaisse les fins de ligne.
const buildCsv = (headers: string[], rows: string[][]): string =>
  "﻿" + [headers, ...rows].map((r) => r.map(escCsv).join(";")).join("\r\n");

const downloadCsv = (filename: string, content: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

type ExportSource = "students" | "staff";

// Champs exportables par source. `get` extrait la valeur d'une ligne.
// Les champs médicaux (groupe sanguin, allergies, notes) sont VOLONTAIREMENT
// absents : un CSV n'est pas soumis à la garde `can_view_sensitive`, on ne
// laisse pas de données de santé s'y déverser.
type FieldDef<T> = { key: string; label: string; get: (row: T) => unknown };

const STUDENT_FIELDS: FieldDef<Student>[] = [
  { key: "massar_code", label: "Code Massar", get: (s) => s.massar_code },
  { key: "last_name", label: "Nom", get: (s) => s.last_name },
  { key: "first_name", label: "Prénom", get: (s) => s.first_name },
  { key: "last_name_ar", label: "Nom (ar)", get: (s) => s.last_name_ar },
  { key: "first_name_ar", label: "Prénom (ar)", get: (s) => s.first_name_ar },
  { key: "gender", label: "Sexe", get: (s) => s.gender },
  { key: "birth_date", label: "Naissance", get: (s) => s.birth_date?.slice(0, 10) },
  { key: "birth_place", label: "Lieu de naissance", get: (s) => s.birth_place },
  { key: "cin", label: "CIN", get: (s) => s.cin },
  { key: "address", label: "Adresse", get: (s) => s.address },
  { key: "city", label: "Ville", get: (s) => s.city },
  { key: "phone", label: "Téléphone", get: (s) => s.phone },
  { key: "email", label: "Email", get: (s) => s.email },
  { key: "status", label: "Statut", get: (s) => s.status },
];
const STUDENT_DEFAULT = ["massar_code", "last_name", "first_name", "gender", "city"];

// subjects se remplit à l'exécution (dépend du référentiel matières).
const STAFF_FIELDS = (subjectCode: (id: number) => string): FieldDef<Staff>[] => [
  { key: "last_name", label: "Nom", get: (s) => s.last_name },
  { key: "first_name", label: "Prénom", get: (s) => s.first_name },
  { key: "role", label: "Fonction", get: (s) => s.role },
  { key: "subjects", label: "Matières", get: (s) => (s.subject_ids ?? []).map(subjectCode).filter(Boolean).join(" ") },
  { key: "email", label: "Email", get: (s) => s.email },
  { key: "phone", label: "Téléphone", get: (s) => s.phone },
  { key: "cin", label: "CIN", get: (s) => s.cin },
  { key: "hire_date", label: "Embauche", get: (s) => s.hire_date?.slice(0, 10) },
  { key: "address", label: "Adresse", get: (s) => s.address },
];
const STAFF_DEFAULT = ["last_name", "first_name", "role", "subjects"];

const ExportTab: React.FC<{ years: SchoolYear[] }> = ({ years }) => {
  const school = useSchoolApi();
  const pedagogy = usePedagogyApi();

  const [source, setSource] = useState<ExportSource>("students");
  const [exporting, setExporting] = useState(false);

  // Filtres élèves
  const [yearId, setYearId] = useState<number>();
  const [classes, setClasses] = useState<{ id: number; label: string }[]>([]);
  const [classId, setClassId] = useState<number>();
  const [status, setStatus] = useState<string>("ACTIF");

  // Filtres personnel
  const [subjects, setSubjects] = useState<{ id: number; code: string; name: string }[]>([]);
  const [subjectId, setSubjectId] = useState<number>();

  // Champs cochés — réinitialisés au défaut de la source choisie.
  const [checked, setChecked] = useState<string[]>(STUDENT_DEFAULT);

  useEffect(() => {
    pedagogy.listSubjects().then(setSubjects).catch(() => undefined);
  }, [pedagogy]);
  useEffect(() => {
    setClassId(undefined);
    if (yearId) school.listClasses(yearId).then((cs) => setClasses(cs)).catch(() => setClasses([]));
    else setClasses([]);
  }, [school, yearId]);

  const onSourceChange = (v: ExportSource) => {
    setSource(v);
    setChecked(v === "students" ? STUDENT_DEFAULT : STAFF_DEFAULT);
  };

  const subjectCode = (id: number) => subjects.find((s) => s.id === id)?.code ?? "";
  const fields: FieldDef<any>[] = source === "students" ? STUDENT_FIELDS : STAFF_FIELDS(subjectCode);
  const selected = fields.filter((f) => checked.includes(f.key));

  const runExport = async () => {
    if (selected.length === 0) {
      message.warning("Sélectionnez au moins un champ à exporter");
      return;
    }
    setExporting(true);
    try {
      let rows: any[];
      let namePart: string;

      if (source === "students") {
        // page_size large : l'export doit tout couvrir, pas une page.
        const res = await school.listStudents({
          school_year_id: yearId,
          class_id: classId,
          status: status || undefined,
          page_size: 5000,
        });
        rows = res.items ?? [];
        namePart = classes.find((c) => c.id === classId)?.label ?? "eleves";
      } else {
        const all = await pedagogy.listStaff();
        rows = all
          .filter((s) => s.status === "ACTIF")
          .filter((s) => (subjectId ? (s.subject_ids ?? []).includes(subjectId) : true));
        namePart = subjectId ? subjectCode(subjectId) : "personnel";
      }

      if (rows.length === 0) {
        message.info("Aucune ligne à exporter avec ces filtres");
        return;
      }

      const csv = buildCsv(
        selected.map((f) => f.label),
        rows.map((r) => selected.map((f) => escCsv(f.get(r))))
      );
      const kind = source === "students" ? "eleves" : "personnel";
      downloadCsv(`export_${kind}_${namePart}_${dayjs().format("YYYY-MM-DD")}.csv`, csv);
      message.success(`${rows.length} ligne(s) exportée(s)`);
    } catch {
      message.error("Export impossible");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <Typography.Paragraph type="secondary">
        Export de travail (Excel) des données déjà saisies. Choisissez la source, filtrez, puis
        cochez les colonnes. Pour le dépôt officiel Massar, utilisez l'onglet « Export Massar ».
      </Typography.Paragraph>

      <Segmented
        value={source}
        onChange={(v) => onSourceChange(v as ExportSource)}
        options={[
          { value: "students", label: "Élèves" },
          { value: "staff", label: "Personnel" },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        {source === "students" ? (
          <>
            <Select
              placeholder="Année scolaire" style={{ width: 160 }} value={yearId} onChange={setYearId} allowClear
              options={years.map((y) => ({ value: y.id, label: y.label }))}
            />
            <Select
              placeholder="Toutes les classes" style={{ width: 180 }} value={classId} onChange={setClassId} allowClear
              disabled={!yearId}
              options={classes.map((c) => ({ value: c.id, label: c.label }))}
            />
            <Select
              style={{ width: 150 }} value={status} onChange={setStatus}
              options={[
                { value: "ACTIF", label: "Actifs" },
                { value: "ARCHIVE", label: "Archivés" },
                { value: "", label: "Tous" },
              ]}
            />
          </>
        ) : (
          <Select
            placeholder="Toutes les matières" style={{ width: 220 }} value={subjectId} onChange={setSubjectId} allowClear
            options={subjects.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
          />
        )}
      </Space>

      <Divider orientation="left" plain>Colonnes à exporter</Divider>
      <Checkbox.Group
        value={checked}
        onChange={(v) => setChecked(v as string[])}
        style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}
      >
        {fields.map((f) => (
          <Checkbox key={f.key} value={f.key} style={{ width: 150, marginInlineStart: 0 }}>
            {f.label}
          </Checkbox>
        ))}
      </Checkbox.Group>

      <Button
        type="primary" icon={<DownloadOutlined />} loading={exporting}
        disabled={selected.length === 0}
        onClick={runExport}
      >
        Exporter le CSV ({selected.length} colonne{selected.length > 1 ? "s" : ""})
      </Button>
    </Card>
  );
};

// ── Massar ──────────────────────────────────────────────────────────────────
const MassarTab: React.FC<{ years: SchoolYear[] }> = ({ years }) => {
  const http = useApiClient("tilmischool");
  const [mappings, setMappings] = useState<any[]>([]);
  const [yearId, setYearId] = useState<number>();

  const load = useCallback(() => {
    http.get("/api/v1/school/massar/mappings").then((r) => setMappings(r.data ?? [])).catch(() => setMappings([]));
  }, [http]);
  useEffect(load, [load]);

  const exportCSV = () => {
    http.get("/api/v1/school/massar/export", { params: { school_year_id: yearId }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = "massar_export.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => message.error("Export refusé — mapping actif requis (et accès direction)"));
  };

  return (
    <Card>
      <Typography.Paragraph type="secondary">
        Le format d'export est un mapping reconfigurable (colonne Massar → champ interne) — modifiable sans
        redéploiement si le format Massar change. Un seul mapping actif à la fois.
      </Typography.Paragraph>
      <Form layout="vertical" style={{ maxWidth: 640 }}
        onFinish={(v) => http.post("/api/v1/school/massar/mappings", v)
          .then(() => { message.success("Mapping créé — pensez à l'activer"); load(); }).catch(apiError)}>
        <Form.Item name="name" label="Nom du mapping" rules={[{ required: true }]}>
          <Input placeholder="Format Massar 2026" />
        </Form.Item>
        <Form.List name="mapping" initialValue={[{ column: "CODE_MASSAR", source: "massar_code" }]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Space key={key} style={{ marginBottom: 4 }}>
                  <Form.Item {...rest} name={[name, "column"]} rules={[{ required: true, message: "Colonne requise" }]} noStyle>
                    <Input placeholder="Colonne Massar" style={{ width: 200 }} />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, "source"]} rules={[{ required: true, message: "Champ cible requis" }]} noStyle>
                    <Select placeholder="Champ interne" style={{ width: 220 }}
                      options={MASSAR_SOURCES.map((s) => ({ value: s, label: s }))} />
                  </Form.Item>
                  <Button type="text" danger onClick={() => remove(name)}>×</Button>
                </Space>
              ))}
              <div><Button type="dashed" onClick={() => add()}>Ajouter une colonne</Button></div>
            </>
          )}
        </Form.List>
        <Button type="primary" htmlType="submit" style={{ marginTop: 12 }}>Enregistrer le mapping</Button>
      </Form>

      <List style={{ marginTop: 16 }} size="small" dataSource={mappings} header="Mappings existants"
        renderItem={(m: any) => (
          <List.Item actions={[
            m.is_active ? <Tag key="a" color="green">ACTIF</Tag>
              : <Button key="b" size="small" onClick={() =>
                  http.post(`/api/v1/school/massar/mappings/${m.id}/activate`).then(load).catch(apiError)}>Activer</Button>]}>
            {m.name} — {(JSON.parse(JSON.stringify(m.mapping)) as any[]).length} colonnes
          </List.Item>)} />

      <Space style={{ marginTop: 16 }}>
        <Select placeholder="Année scolaire" style={{ width: 180 }} value={yearId} onChange={setYearId}
          options={years.map((y) => ({ value: y.id, label: y.label }))} />
        <Button type="primary" icon={<DownloadOutlined />} disabled={!yearId} onClick={exportCSV}>
          Exporter le CSV Massar
        </Button>
      </Space>
    </Card>
  );
};

// ── Documents ───────────────────────────────────────────────────────────────
const DocsTab: React.FC<{ years: SchoolYear[] }> = ({ years }) => {
  const http = useApiClient("tilmischool");
  const [form] = Form.useForm();
  const [docs, setDocs] = useState<any[]>([]);
  const load = useCallback(() => {
    http.get("/api/v1/school/org-documents").then((r) => setDocs(r.data ?? [])).catch(() => setDocs([]));
  }, [http]);
  useEffect(load, [load]);

  return (
    <Card>
      {/* Le fichier part vers tc_upload dès la sélection (DocumentUpload) et son
          URL renseigne file_url ; on repasse en layout vertical car le bouton
          d'upload et sa confirmation ne tiennent pas sur une seule ligne. */}
      <Form form={form} layout="vertical" style={{ maxWidth: 640, marginBottom: 12 }}
        onFinish={(v) => http.post("/api/v1/school/org-documents", v)
          .then(() => { message.success("Document publié"); form.resetFields(); load(); }).catch(apiError)}>
        <Space wrap align="start">
          <Form.Item name="category" label="Catégorie" initialValue="CIRCULAIRE">
            <Select style={{ width: 200 }} options={["REGLEMENT_INTERIEUR", "CIRCULAIRE", "AUTRE"].map((c) => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="title" label="Titre" rules={[{ required: true }]}>
            <Input placeholder="Titre" style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="school_year_id" label="Année (opt.)">
            <Select allowClear placeholder="Toutes les années" style={{ width: 160 }}
              options={years.map((y) => ({ value: y.id, label: y.label }))} />
          </Form.Item>
        </Space>
        <Form.Item name="file_url" label="Fichier" valuePropName="value" rules={[{ required: true, message: "Joindre un fichier" }]}>
          <DocumentUpload
            onFileName={(name) => { if (!form.getFieldValue("title")) form.setFieldValue("title", name); }}
          />
        </Form.Item>
        <Form.Item shouldUpdate noStyle>
          {() => (
            <Button htmlType="submit" type="primary" disabled={!form.getFieldValue("file_url")}>Publier</Button>
          )}
        </Form.Item>
      </Form>
      <List size="small" dataSource={docs} locale={{ emptyText: "Aucun document" }}
        renderItem={(d: any) => (
          <List.Item actions={[
            <a key="o" href={d.file_url} target="_blank" rel="noreferrer">Ouvrir</a>,
            <Popconfirm key="x" title="Supprimer ?" onConfirm={() =>
              http.delete(`/api/v1/school/org-documents/${d.id}`).then(load).catch(apiError)}>
              <Button size="small" type="text" danger>Supprimer</Button>
            </Popconfirm>]}>
            <List.Item.Meta title={d.title} description={<Tag>{d.category}</Tag>} />
          </List.Item>)} />
    </Card>
  );
};

// ── Bibliothèque ────────────────────────────────────────────────────────────
const LibraryTab: React.FC = () => {
  const http = useApiClient("tilmischool");
  const school = useSchoolApi();
  const [books, setBooks] = useState<any[]>([]);
  const [loans, setLoans] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);

  const load = useCallback(() => {
    http.get("/api/v1/school/library/books").then((r) => setBooks(r.data ?? []));
    http.get("/api/v1/school/library/loans?open=true").then((r) => setLoans(r.data ?? []));
  }, [http]);
  useEffect(load, [load]);
  useEffect(() => {
    school.listStudents({ page_size: 200, status: "ACTIF" }).then((r) => setStudents(r.items ?? [])).catch(() => undefined);
  }, [school]);

  return (
    <Card extra={WHATSAPP_ENABLED ? (
      <Popconfirm title="Relancer les retards via WhatsApp (cooldown 7 j) ?"
        onConfirm={() => http.post("/api/v1/school/library/reminders")
          .then((r) => { message.success(`${r.data.sent} relance(s)`); load(); }).catch(apiError)}>
        <Button icon={<WhatsAppOutlined />}>Relancer les retards</Button>
      </Popconfirm>) : undefined}>
      <Form layout="inline" style={{ marginBottom: 12 }}
        onFinish={(v) => http.post("/api/v1/school/library/books", v).then(load).catch(apiError)}>
        <Form.Item name="title" rules={[{ required: true, message: "Titre requis" }]}><Input placeholder="Titre" style={{ width: 200 }} /></Form.Item>
        <Form.Item name="author"><Input placeholder="Auteur" style={{ width: 160 }} /></Form.Item>
        <Form.Item name="copies_total" initialValue={1}><InputNumber min={1} /></Form.Item>
        <Button htmlType="submit">Ajouter au catalogue</Button>
      </Form>
      <ResponsiveTable<any> rowKey="id" dataSource={books} pagination={false} size="small"
        card={{
          title: (b) => b.title,
          extra: (b) => (
            <Tag bordered={false} color={b.copies_out >= b.copies_total ? "red" : "green"}>
              {b.copies_total - b.copies_out}/{b.copies_total}
            </Tag>
          ),
          description: (b) => (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{b.author || "Auteur inconnu"}</Typography.Text>
              <PrêtForm disabled={b.copies_out >= b.copies_total} students={students}
                onLoan={(sid, due) => http.post("/api/v1/school/library/loans",
                  { book_id: b.id, student_id: sid, due_date: due }).then(load).catch(apiError)} />
            </>
          ),
        }}
        columns={[
          { title: "Titre", dataIndex: "title" },
          { title: "Auteur", dataIndex: "author", render: (v) => v || "—" },
          { title: "Disponibles", render: (_, b: any) => `${b.copies_total - b.copies_out}/${b.copies_total}` },
          { title: "Prêter à", render: (_, b: any) => (
            <PrêtForm disabled={b.copies_out >= b.copies_total} students={students}
              onLoan={(sid, due) => http.post("/api/v1/school/library/loans",
                { book_id: b.id, student_id: sid, due_date: due }).then(load).catch(apiError)} />) },
        ]} />
      <Typography.Title level={5} style={{ marginTop: 16 }}>Prêts en cours</Typography.Title>
      <ResponsiveTable<any> rowKey="id" dataSource={loans} pagination={false} size="small"
        card={{
          title: (l) => l.book_title,
          extra: (l) => (
            <Tag color={dayjs(l.due_date).isBefore(dayjs(), "day") ? "red" : "default"}>
              {dayjs(l.due_date).format("DD/MM/YYYY")}
            </Tag>
          ),
          description: (l) => (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {l.student_name}
              {l.reminded_at ? ` · relancé le ${dayjs(l.reminded_at).format("DD/MM")}` : ""}
            </Typography.Text>
          ),
          actions: (l) => [
            <Button key="ret" size="small" type="text" onClick={() =>
              http.post(`/api/v1/school/library/loans/${l.id}/return`).then(load).catch(apiError)}>Retour</Button>,
          ],
        }}
        columns={[
          { title: "Livre", dataIndex: "book_title" },
          { title: "Élève", dataIndex: "student_name" },
          { title: "Échéance", dataIndex: "due_date", render: (v: string) => {
            const overdue = dayjs(v).isBefore(dayjs(), "day");
            return <Tag color={overdue ? "red" : "default"}>{dayjs(v).format("DD/MM/YYYY")}</Tag>;
          } },
          { title: "Relancé", dataIndex: "reminded_at", render: (v?: string) => (v ? dayjs(v).format("DD/MM") : "—") },
          { title: "", render: (_, l: any) => (
            <Button size="small" onClick={() =>
              http.post(`/api/v1/school/library/loans/${l.id}/return`).then(load).catch(apiError)}>Retour</Button>) },
        ]} />
    </Card>
  );
};

const PrêtForm: React.FC<{ disabled: boolean; students: any[]; onLoan: (sid: number, due: string) => void }> =
  ({ disabled, students, onLoan }) => {
    const [sid, setSid] = useState<number>();
    const [due, setDue] = useState(dayjs().add(14, "day"));
    return (
      <Space>
        <Select showSearch disabled={disabled} placeholder="Élève" style={{ width: 180 }} value={sid} onChange={setSid}
          optionFilterProp="label"
          options={students.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
        <DatePicker value={due} onChange={(d) => d && setDue(d)} format="DD/MM/YYYY" />
        <Button size="small" disabled={disabled || !sid}
          onClick={() => onLoan(sid!, due.format("YYYY-MM-DD") + "T00:00:00Z")}>Prêter</Button>
      </Space>
    );
  };

// ── Transport ───────────────────────────────────────────────────────────────
const TransportTab: React.FC = () => {
  const http = useApiClient("tilmischool");
  const school = useSchoolApi();
  const [routes, setRoutes] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [selected, setSelected] = useState<number>();
  const [assignments, setAssignments] = useState<any[]>([]);

  const load = useCallback(() => {
    http.get("/api/v1/school/transport/routes").then((r) => setRoutes(r.data ?? []));
    if (selected) http.get(`/api/v1/school/transport/routes/${selected}/students`).then((r) => setAssignments(r.data ?? []));
  }, [http, selected]);
  useEffect(load, [load]);
  useEffect(() => {
    school.listStudents({ page_size: 200, status: "ACTIF" }).then((r) => setStudents(r.items ?? [])).catch(() => undefined);
  }, [school]);

  return (
    <Card>
      <Form layout="inline" style={{ marginBottom: 12 }}
        onFinish={(v) => http.post("/api/v1/school/transport/routes", v).then(load).catch(apiError)}>
        <Form.Item name="name" rules={[{ required: true, message: "Nom requis" }]}><Input placeholder="Nom de la ligne" style={{ width: 170 }} /></Form.Item>
        <Form.Item name="driver_name"><Input placeholder="Conducteur" style={{ width: 150 }} /></Form.Item>
        <Form.Item name="driver_phone"><Input placeholder="Tél. conducteur" style={{ width: 150 }} /></Form.Item>
        <Form.Item name="vehicle"><Input placeholder="Véhicule" style={{ width: 120 }} /></Form.Item>
        <Button htmlType="submit">Créer la ligne</Button>
      </Form>
      <ResponsiveTable<any> rowKey="id" dataSource={routes} pagination={false} size="small"
        onRow={(r: any) => ({ onClick: () => setSelected(r.id) })}
        onRowClick={(r) => setSelected(r.id)}
        card={{
          title: (r) => r.name,
          extra: (r) => <Tag bordered={false}>{r.student_count} élève(s)</Tag>,
          description: (r) => (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.driver_name ? `${r.driver_name}${r.driver_phone ? " · " + r.driver_phone : ""}` : "Aucun conducteur"}
            </Typography.Text>
          ),
          actions: (r) => [
            ...(WHATSAPP_ENABLED ? [
              <NotifyRoute key="notify" onNotify={(info) =>
                http.post(`/api/v1/school/transport/routes/${r.id}/notify`, { info })
                  .then((res) => message.success(`${res.data.sent} notification(s) WhatsApp`)).catch(apiError)} />,
            ] : []),
            <Popconfirm key="del" title="Supprimer la ligne ?" onConfirm={() =>
              http.delete(`/api/v1/school/transport/routes/${r.id}`).then(load).catch(apiError)}>
              <Button size="small" danger type="text">Supprimer</Button>
            </Popconfirm>,
          ],
        }}
        columns={[
          { title: "Ligne", dataIndex: "name", render: (v, r: any) => (
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSelected(r.id)}>{v}</Button>) },
          { title: "Conducteur", render: (_, r: any) => r.driver_name ? `${r.driver_name}${r.driver_phone ? " · " + r.driver_phone : ""}` : "—" },
          { title: "Élèves", dataIndex: "student_count" },
          { title: "", render: (_, r: any) => (
            <Space>
              {WHATSAPP_ENABLED && (
                <NotifyRoute onNotify={(info) =>
                  http.post(`/api/v1/school/transport/routes/${r.id}/notify`, { info })
                    .then((res) => message.success(`${res.data.sent} notification(s) WhatsApp`)).catch(apiError)} />
              )}
              <Popconfirm title="Supprimer la ligne ?" onConfirm={() =>
                http.delete(`/api/v1/school/transport/routes/${r.id}`).then(load).catch(apiError)}>
                <Button size="small" danger type="text">Supprimer</Button>
              </Popconfirm>
            </Space>) },
        ]} />
      {selected && (
        <>
          <Typography.Title level={5} style={{ marginTop: 16 }}>
            Élèves de la ligne {routes.find((r) => r.id === selected)?.name}
          </Typography.Title>
          <AssignForm students={students}
            onAssign={(sid, stop) => http.post(`/api/v1/school/transport/routes/${selected}/students`,
              { student_id: sid, stop_label: stop || undefined }).then(load).catch(apiError)} />
          <List size="small" dataSource={assignments}
            renderItem={(a: any) => (
              <List.Item actions={[
                <Button key="x" size="small" type="text" danger onClick={() =>
                  http.delete(`/api/v1/school/transport/routes/${selected}/students/${a.student_id}`)
                    .then(load).catch(apiError)}>Retirer</Button>]}>
                {a.student_name}{a.stop_label ? ` — ${a.stop_label}` : ""}
              </List.Item>)} />
        </>
      )}
    </Card>
  );
};

const AssignForm: React.FC<{ students: any[]; onAssign: (sid: number, stop: string) => void }> = ({ students, onAssign }) => {
  const [sid, setSid] = useState<number>();
  const [stop, setStop] = useState("");
  return (
    <Space style={{ marginBottom: 8 }}>
      <Select showSearch placeholder="Élève" style={{ width: 200 }} value={sid} onChange={setSid}
        optionFilterProp="label" options={students.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
      <Input placeholder="Arrêt" value={stop} onChange={(e) => setStop(e.target.value)} style={{ width: 160 }} />
      <Button size="small" disabled={!sid} onClick={() => { onAssign(sid!, stop); setSid(undefined); setStop(""); }}>Affecter</Button>
    </Space>
  );
};

const NotifyRoute: React.FC<{ onNotify: (info: string) => void }> = ({ onNotify }) => {
  const [info, setInfo] = useState("");
  return (
    <Space>
      <Input size="small" placeholder="Message (retard, changement…)" value={info}
        onChange={(e) => setInfo(e.target.value)} style={{ width: 220 }} />
      <Button size="small" icon={<WhatsAppOutlined />} disabled={!info.trim()}
        onClick={() => { onNotify(info); setInfo(""); }}>Notifier</Button>
    </Space>
  );
};

export default AdministrationPage;
